import React, { useState, useEffect, useCallback } from 'react';
import { AtSign, Check, X, Loader2, Sparkles } from 'lucide-react';
import { useAuth, getAuthHeaders } from '../context/AuthContext';
import { BACKEND_URL } from '../config/api';

const HandlePickerModal = ({ onHandleSet }) => {
  const { user, logout } = useAuth();
  const [handle, setHandle] = useState('');
  const [status, setStatus] = useState(null); // null | 'checking' | 'available' | 'taken' | 'invalid'
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState(null);

  // Fetch suggestions on mount
  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/users/handle-suggestions`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data.suggestions || []);
        }
      } catch {
        // Suggestions are optional
      }
    };
    fetchSuggestions();
  }, []);

  const checkAvailability = useCallback(async (value) => {
    if (!value || value.length < 3) {
      setStatus(value.length > 0 ? 'invalid' : null);
      setError(value.length > 0 ? 'At least 3 characters' : '');
      return;
    }
    setStatus('checking');
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/check-handle/${encodeURIComponent(value)}`);
      const data = await res.json();
      if (data.available) {
        setStatus('available');
        setError('');
      } else {
        setStatus(data.reason ? 'invalid' : 'taken');
        setError(data.reason || 'This handle is already taken');
      }
    } catch {
      setStatus(null);
    }
  }, []);

  const handleInputChange = (e) => {
    const raw = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    const value = raw.slice(0, 15);
    setHandle(value);

    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => checkAvailability(value), 400);
    setDebounceTimer(timer);
  };

  const handleSuggestionClick = (s) => {
    setHandle(s.handle);
    if (s.available) {
      setStatus('available');
      setError('');
    } else {
      checkAvailability(s.handle);
    }
  };

  const handleSubmit = async () => {
    if (status !== 'available' || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/set-handle`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ handle }),
      });
      if (res.ok) {
        onHandleSet();
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to set handle');
        setStatus('taken');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    await logout();
    window.location.href = '/';
  };

  const statusIcon = () => {
    if (status === 'checking') return <Loader2 size={18} className="animate-spin text-gray-400" />;
    if (status === 'available') return <Check size={18} className="text-green-500" />;
    if (status === 'taken' || status === 'invalid') return <X size={18} className="text-red-400" />;
    return null;
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4" data-testid="handle-picker-screen">
      <div className="bg-white rounded-2xl border border-beige shadow-lg max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-sage/5 border-b border-beige px-6 py-5 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Sparkles className="h-6 w-6 text-sage" />
            <span className="text-xl font-light text-charcoal">Kura Stash</span>
          </div>
          <h2 className="text-lg font-medium text-charcoal">Choose your handle</h2>
          <p className="text-sm text-charcoal/60 mt-1">
            This is how other crafters will find and recognize you
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {/* Handle input */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-charcoal mb-2">Your handle</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <AtSign size={18} />
              </span>
              <input
                type="text"
                value={handle}
                onChange={handleInputChange}
                placeholder="your_handle"
                maxLength={15}
                className={`w-full pl-10 pr-10 py-3 border-2 rounded-xl text-charcoal placeholder:text-gray-300 focus:outline-none transition-colors ${
                  status === 'available'
                    ? 'border-green-300 focus:border-green-400'
                    : status === 'taken' || status === 'invalid'
                    ? 'border-red-300 focus:border-red-400'
                    : 'border-beige focus:border-sage'
                }`}
                autoFocus
                data-testid="handle-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2">
                {statusIcon()}
              </span>
            </div>
            {error && (
              <p className="text-xs text-red-500 mt-1.5" data-testid="handle-error">{error}</p>
            )}
            {status === 'available' && (
              <p className="text-xs text-green-600 mt-1.5" data-testid="handle-available">
                @{handle} is available!
              </p>
            )}
            <p className="text-xs text-gray-400 mt-1.5">
              Lowercase letters, numbers, underscores. 3-15 characters.
            </p>
          </div>

          {/* Suggestions */}
          {suggestions.length > 0 && !handle && (
            <div className="mb-5">
              <p className="text-xs text-charcoal/60 mb-2">Suggestions for you</p>
              <div className="flex flex-wrap gap-2">
                {suggestions.filter((s, i, arr) => arr.findIndex(x => x.handle === s.handle) === i).map((s) => (
                  <button
                    key={s.handle}
                    onClick={() => handleSuggestionClick(s)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      s.available
                        ? 'border-sage/30 text-sage hover:bg-sage/5'
                        : 'border-beige text-gray-400 line-through'
                    }`}
                    disabled={!s.available}
                    data-testid={`suggestion-${s.handle}`}
                  >
                    @{s.handle}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              className="flex-1 py-2.5 rounded-lg text-sm font-medium text-gray-soft border border-beige hover:bg-cream transition-all"
              data-testid="handle-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={status !== 'available' || submitting}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                status === 'available' && !submitting
                  ? 'bg-sage text-white hover:bg-sage-dark active:scale-[0.99]'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              data-testid="handle-submit-btn"
            >
              {submitting ? 'Setting...' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HandlePickerModal;
